import { Box, Stepper, Step, StepLabel, Tooltip, Typography, Divider, useTheme, useMediaQuery } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { cloneElement } from "react";
import { colors } from "../../ui/colors";

type Props = {
  currStep: number;
  stepsConfig: Array<{
    id?: number | string;
    name: string;
    description?: string;
    icon: any;
    disableStepper?: boolean;
  }>;
  hideStepper?: boolean;
  showCurrent?: boolean;
};

export const JobStepper = ({ currStep, stepsConfig, hideStepper = false, showCurrent = true }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const current = stepsConfig?.[currStep];
  const currentLabel = showCurrent && current ? `${currStep + 1}/${stepsConfig.length} · ${current.name}` : "";

  const shouldHideStepper = hideStepper || (current?.disableStepper ?? false);

  if (shouldHideStepper) return null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        mx: "auto",
        mt: 1,
        px: { xs: 1, sm: 2, md: 0 },

        overflowX: { xs: "hidden", sm: "visible" },
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            marginLeft: 5,
            gap: 2,
            width: "100%",
            maxWidth: 250,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: "bold", color: colors.orange[600], fontSize: "16px", wordBreak: "break-word" }}>
            {currentLabel}
          </Typography>

          {/*box para el icono*/}
          <Box
            sx={{
              backgroundColor: colors.orange[400],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              width: 32,
              height: 32,
            }}
          >
            {current?.icon && cloneElement(current.icon, { sx: { fontSize: "1.25rem", color: "white" } })}
          </Box>
        </Box>
      ) : (
        <>
          <Stepper
            activeStep={currStep}
            alternativeLabel
            orientation="horizontal"
            sx={{
              bgcolor: "transparent",
              px: 2,
              "& .MuiStepConnector-line": {
                borderColor: colors.orange[200],
                borderWidth: 2,
              },
              "& .MuiStep-root": {
                flex: "1 1 0",
                minWidth: 0,
              },
            }}
          >
            {stepsConfig?.map((step, index) => {
              const completed = currStep > index;
              const isActive = currStep === index;

              const Icon = completed ? <CheckCircle sx={{ fontSize: "1.25rem" }} /> : cloneElement(step.icon, { sx: { fontSize: "1.25rem" } });

              return (
                <Step key={step.id ?? index} completed={completed}>
                  <Tooltip title={step.description ?? ""} arrow enterTouchDelay={500} leaveTouchDelay={3000}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            bgcolor: completed ? colors.orange[100] : isActive ? colors.orange.base : colors.gray[100],
                            color: completed ? colors.orange.base : isActive ? "#fff" : colors.gray[400],
                            border: `2px solid ${completed ? colors.orange[300] : isActive ? colors.orange.base : colors.gray[200]}`,
                            transition: "all 0.2s ease",
                          }}
                        >
                          {Icon}
                        </Box>
                      )}
                      sx={{
                        mt: 1,
                        textAlign: "center",
                        "& .MuiStepLabel-label": {
                          typography: "caption",
                          mt: 1,
                          color: isActive ? colors.orange.base : completed ? colors.gray[600] : colors.gray[400],
                          fontWeight: isActive || completed ? 600 : 400,
                        },
                      }}
                    >
                      <Typography variant="caption" noWrap>
                        {step.name}
                      </Typography>
                    </StepLabel>
                  </Tooltip>
                </Step>
              );
            })}
          </Stepper>

          <Divider sx={{ mt: 2 }} />
        </>
      )}
    </Box>
  );
};
